import { useEffect, useRef, useState } from 'react';
import { DURATION } from '@constants/motion';

const NONE: ReadonlySet<string> = new Set();

/**
 * The keys of a live list that appeared since the last render (not on the first one), for `ms`: a newly arrived row
 * gets one short highlight (the motion standard's `.is-new`). Only the keys on screen are remembered; a later change
 * of the list does not cut a highlight short.
 */
export function useArrivals(keys: readonly string[], ms: number = DURATION.highlight): ReadonlySet<string> {
  const seen = useRef<Set<string> | null>(null);
  const timer = useRef(0);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(NONE);
  const sig = keys.join('|');
  useEffect(() => {
    const before = seen.current;
    seen.current = new Set(keys);
    if (!before) return;
    const added = keys.filter((k) => !before.has(k));
    if (!added.length) return;
    setFresh(new Set(added));
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setFresh(NONE), ms);
  }, [sig, ms]); // eslint-disable-line react-hooks/exhaustive-deps -- sig stands for keys
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return fresh;
}
